import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import APP_BASE_URL from "../components/Api/globalApi";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);

  const getAuthConfig = () => {
    const token = localStorage.getItem("virtualmall_token");
    if (!token) return null;
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const fetchCart = useCallback(async () => {
    const authConfig = getAuthConfig();
    if (!authConfig) {
      setCartItems([]);
      return [];
    }

    try {
      setCartLoading(true);
      const response = await axios.get(`${APP_BASE_URL}/virtualuser/cart`, authConfig);
      const items = response.data?.data || [];
      setCartItems(items);
      return items;
    } catch (error) {
      console.error("Failed to fetch cart", error);
      return [];
    } finally {
      setCartLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (product) => {
    const authConfig = getAuthConfig();
    if (!authConfig || !product?.vsp_id) return false;
    try {
      await axios.post(
        `${APP_BASE_URL}/virtualuser/cart/add`,
        { vsp_id: product.vsp_id, quantity: 1 },
        authConfig
      );
      await fetchCart();
      return true;
    } catch (error) {
      console.error("Failed to add cart item", error);
      return false;
    }
  };

  const removeFromCart = async (productId) => {
    const authConfig = getAuthConfig();
    if (!authConfig) return;
    try {
      await axios.post(`${APP_BASE_URL}/virtualuser/cart/remove`, { vsp_id: productId }, authConfig);
      await fetchCart();
    } catch (error) {
      console.error("Failed to remove cart item", error);
    }
  };

  const incrementQuantity = async (productId) => {
    const authConfig = getAuthConfig();
    if (!authConfig) return;
    const existing = cartItems.find((item) => item.vsp_id === productId);
    if (!existing) return;
    try {
      await axios.post(
        `${APP_BASE_URL}/virtualuser/cart/update-qty`,
        { vsp_id: productId, quantity: existing.quantity + 1 },
        authConfig
      );
      await fetchCart();
    } catch (error) {
      console.error("Failed to increment cart item", error);
    }
  };

  const decrementQuantity = async (productId) => {
    const authConfig = getAuthConfig();
    if (!authConfig) return;
    const existing = cartItems.find((item) => item.vsp_id === productId);
    if (!existing || existing.quantity <= 1) return;
    try {
      await axios.post(
        `${APP_BASE_URL}/virtualuser/cart/update-qty`,
        { vsp_id: productId, quantity: existing.quantity - 1 },
        authConfig
      );
      await fetchCart();
    } catch (error) {
      console.error("Failed to decrement cart item", error);
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        incrementQuantity,
        decrementQuantity,
        clearCart,
        fetchCart,
        cartLoading,
        cartItemCount: cartItems.length,
        cartTotalCount: cartItems.reduce((acc, item) => acc + item.quantity, 0),
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
